/**
 * MessageBubble - Telegram-like message bubble component
 * Compact, bubble-style message display optimized for the context panel
 */

import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import { Message } from '@/types/api';
import { format, parseISO } from 'date-fns';
import { TelegramLinkButton } from '@/components/common/TelegramLinkButton';

interface MessageBubbleProps {
  message: Message;
  isTarget?: boolean;
  messageType?: 'TITLE' | 'SEPARATOR' | 'MEDIA' | null;
}

/**
 * Format timestamp for compact display (e.g., "10:23 AM")
 */
const formatTime = (dateString: string | null): string => {
  if (!dateString) return '';

  try {
    const date = parseISO(dateString);
    return format(date, 'h:mm a');
  } catch (error) {
    return '';
  }
};

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isTarget = false,
  messageType = null,
}) => {
  const hasText = !!message.message_text && message.message_text.trim() !== '';
  const hasMedia = !!message.media;

  // Determine background color based on message type
  const getBackgroundColor = () => {
    if (messageType === 'TITLE') return 'primary.light';
    if (messageType === 'SEPARATOR') return 'warning.light';
    if (messageType === 'MEDIA') return 'success.light';
    return 'action.hover';
  };

  return (
    <Box
      id={`message-${message.message_id}`}
      sx={{
        position: 'relative',
        mb: 1,
        p: 1.5,
        bgcolor: getBackgroundColor(),
        borderRadius: 2,
        maxWidth: '100%',
        border: isTarget ? 2 : 0,
        borderColor: isTarget ? 'primary.main' : 'transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: getBackgroundColor(),
          opacity: 0.9,
        },
        '& .telegram-link-overlay': {
          opacity: 0,
          transition: 'opacity 0.2s',
        },
        '&:hover .telegram-link-overlay': {
          opacity: 1,
        },
      }}
    >
      <Box
        className="telegram-link-overlay"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
        }}
      >
        <TelegramLinkButton
          chatId={message.chats_id}
          messageId={message.message_id}
          size="small"
        />
      </Box>

      {/* Message ID and Timestamp */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          #{message.message_id}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTime(message.timestamp)}
        </Typography>
      </Box>

      {/* Message Text */}
      {hasText && (
        <Typography
          variant="body2"
          sx={{
            direction: 'rtl',
            textAlign: 'right',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            mb: hasMedia ? 1 : 0,
          }}
        >
          {message.message_text}
        </Typography>
      )}

      {/* Media Indicator */}
      {hasMedia && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: hasText ? 1 : 0,
            p: 0.5,
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <ImageIcon fontSize="small" color="action" />
          <Typography variant="caption" color="text.secondary" noWrap>
            {message.media?.file_name || 'Media file'}
          </Typography>
        </Box>
      )}

      {/* Target Indicator */}
      {isTarget && (
        <Box sx={{ mt: 0.5 }}>
          <Chip label="Target Message" size="small" color="primary" sx={{ height: 20 }} />
        </Box>
      )}

      {/* Empty message indicator */}
      {!hasText && !hasMedia && (
        <Typography variant="caption" color="text.secondary" fontStyle="italic">
          [Empty message]
        </Typography>
      )}
    </Box>
  );
};

export default MessageBubble;
